import xhr from 'xhr';

/**
 * Panel Monitoring › Calificación de actividades.
 * Endpoints en klazia-api: src/api/ai-grading-panel (solo admin y supervisor_admin).
 */

function query(params) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export async function getAiSummary(params) {
  const { data } = await xhr.get(`/ai-grading/summary${query(params)}`);
  return data;
}

export async function getAiFilters() {
  const { data } = await xhr.get('/ai-grading/filters');
  return data;
}

export async function getAiSittings(params) {
  const { data } = await xhr.get(`/ai-grading/sittings${query(params)}`);
  return data;
}

export async function getAiSitting(id) {
  const { data } = await xhr.get(`/ai-grading/sittings/${id}`);
  return data;
}

export async function getAiPrechecks(params) {
  const { data } = await xhr.get(`/ai-grading/prechecks${query(params)}`);
  return data;
}

export async function getAiRuns(params) {
  const { data } = await xhr.get(`/ai-grading/runs${query(params)}`);
  return data;
}

export async function getAiRun(id) {
  const { data } = await xhr.get(`/ai-grading/runs/${id}`);
  return data;
}

// ——— Acciones (quedan en Auditoría) ———

export async function sittingAction(id, action, reason) {
  const { data } = await xhr.post(`/ai-grading/sittings/${id}/${action}`, { reason });
  return data;
}

export async function pauseRelease(reason) {
  const { data } = await xhr.post('/ai-grading/release/pause', { reason });
  return data;
}

export async function resumeRelease() {
  const { data } = await xhr.post('/ai-grading/release/resume', {});
  return data;
}

export async function revertRun(id, { mode, pauseSubjects, reason }) {
  const { data } = await xhr.post(`/ai-grading/runs/${id}/revert`, { mode, pauseSubjects, reason });
  return data;
}

export async function resumeSubjects(subjectIds) {
  const { data } = await xhr.post('/ai-grading/subjects/resume', { subjectIds });
  return data;
}

// ——— Activación por programa o materia ———

export async function getActivation(params) {
  const { data } = await xhr.get(`/ai-grading/activation${query(params)}`);
  return data;
}

export async function previewActivation(changes) {
  const { data } = await xhr.post('/ai-grading/activation/preview', { changes });
  return data;
}

export async function applyActivation(changes, reason) {
  const { data } = await xhr.post('/ai-grading/activation/apply', { changes, reason });
  return data;
}

export const ACTIVATION_STATE = {
  on: { label: 'Activa', tone: 'green', dot: '#10B981' },
  mixed: { label: 'Parcial', tone: 'blue', dot: '#6366F1' },
  off: { label: 'Inactiva', tone: 'gray', dot: '#94A3B8' },
};

// ——— Pruebas (califican una muestra sin publicar) ———

export async function getTestOptions() {
  const { data } = await xhr.get('/ai-grading/tests/options');
  return data;
}

export async function estimateTest(body) {
  const { data } = await xhr.post('/ai-grading/tests/estimate', body);
  return data;
}

export async function createTest(body) {
  const { data } = await xhr.post('/ai-grading/tests', body);
  return data;
}

export async function getTests(params) {
  const { data } = await xhr.get(`/ai-grading/tests${query(params)}`);
  return data;
}

export async function getTest(id, params) {
  const { data } = await xhr.get(`/ai-grading/tests/${id}${query(params)}`);
  return data;
}

export async function publishTest(id, sittingIds) {
  const { data } = await xhr.post(`/ai-grading/tests/${id}/publish`, { sittingIds });
  return data;
}

export async function discardTest(id) {
  const { data } = await xhr.post(`/ai-grading/tests/${id}/discard`, {});
  return data;
}

export const TEST_PHASE = {
  running: { label: 'En curso', tone: 'blue' },
  review: { label: 'Lista para revisar', tone: 'purple' },
  compared: { label: 'Comparación', tone: 'gray' },
  published: { label: 'Publicada', tone: 'green' },
  discarded: { label: 'Descartada', tone: 'gray' },
  failed: { label: 'Falló', tone: 'red' },
};

/** Mensaje legible de un error del API del panel. */
export const errorMessage = (e) => e?.response?.data?.error?.message || 'No se pudo completar la acción.';

export const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hoy' },
  { value: '7', label: '7 días' },
  { value: '30', label: '30 días' },
];

// Tonos de las pastillas de estado (mismos colores que Clases grabadas).
export const TONES = {
  green: { bg: '#ECFDF5', color: '#047857', border: '#A7F3D0' },
  blue: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  red: { bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
  orange: { bg: '#FFFBEB', color: '#B45309', border: '#FDE68A' },
  gray: { bg: '#F1F5F9', color: '#475569', border: '#E2E8F0' },
  purple: { bg: '#F5F3FF', color: '#5B21B6', border: '#DDD6FE' },
};

export const SITTING_STATUS = {
  queued: { label: 'En cola', tone: 'gray' },
  to_publish: { label: 'Por publicar', tone: 'blue' },
  held: { label: 'Retenida', tone: 'purple' },
  published: { label: 'Publicada', tone: 'green' },
  teacher: { label: 'Al docente', tone: 'orange' },
  teacher_graded: { label: 'Calificada por el docente', tone: 'purple' },
};

// Motivos por los que la calificación automática pasa una entrega al docente.
export const REASON_LABELS = {
  NO_SUBMISSION_CONTENT: 'Entrega sin contenido',
  NO_EXTRACTABLE_TEXT: 'No se pudo leer el archivo',
  NO_CONTENT_FENCE: 'Entrega sin contenido',
  NO_QUESTION: 'La actividad no tiene enunciado',
  ATTACHMENT_FETCH_FAIL: 'No se pudo descargar el archivo',
  IA_REQUEST_ERROR: 'Error del servicio (3 intentos)',
  IA_RESPONSE_INVALID: 'Respuesta inválida del servicio',
  IA_CONTRADICTS_ATTACHMENTS: 'Revisión inconsistente con los adjuntos',
  AI_SCORE_BELOW_THRESHOLD: 'Nota muy baja: revisión del docente',
  VISION_ONLY_LOW_SCORE: 'Nota baja con solo imágenes',
  UNREADABLE: 'Archivo ilegible',
  SAVE_FAILED: 'No se pudo guardar el resultado',
  RELEASE_FAILED: 'No se pudo publicar',
  SENT_BY_ADMIN: 'Enviada al docente desde el panel',
  PRECHECK_OVERRIDE: 'El estudiante la envió aunque no se leía bien',
};

export const reasonLabel = (code) => (code ? REASON_LABELS[code] || code : null);

// Revisión previa del envío guiado: motivo general y problema por página.
export const PRECHECK_REASON = {
  unreadable: 'No se lee bien',
  cut_off: 'Páginas cortadas',
  wrong_orientation: 'Páginas giradas',
  empty: 'En blanco o sin desarrollo',
  not_matching: 'No corresponde a la actividad',
  error: 'No se pudo revisar',
};
export const PAGE_ISSUE = { unreadable: 'Borrosa', cut_off: 'Cortada', wrong_orientation: 'Girada', empty: 'En blanco' };
export const precheckReason = (code) => (code ? PRECHECK_REASON[code] || code : null);

export const RUN_KIND = {
  grade: { label: 'Calificación', dot: '#574feb' },
  release: { label: 'Publicación', dot: '#10B981' },
  test: { label: 'Prueba', dot: '#8B5CF6' },
  revert: { label: 'Reversión', dot: '#EF4444' },
  action: { label: 'Acción manual', dot: '#94A3B8' },
};

export const RUN_ALERTS = {
  MANY_TO_TEACHER: (a) => `El ${a.share} % pasó al docente.`,
  AVG_OFF: (a) => `Nota promedio ${String(a.avg).replace('.', ',')} frente a ${String(a.baseline).replace('.', ',')} de los últimos 7 días.`,
  RUN_FAILED: () => 'La ejecución terminó con error.',
};

export const formatScore = (n) => (n === null || n === undefined ? '—' : Number(n).toFixed(1).replace('.', ','));
